import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { useIsMobile } from "../useMedia";
import type { Meta, SessionUser } from "../types";
import { AI_ENABLED } from "../features";
import ReminderBell from "./ReminderBell";

/** The sections the top menu switches between. */
export type Section = "patients" | "operations" | "stock" | "appointments" | "admin";

/** Что мобильный поиск отдаёт странице пациентов — её же фильтры. */
export interface MobileSearch {
  query: string;
  diag: boolean;
  okt: boolean;
  doctor: string;
  month: number;
  year: number;
}

/**
 * template_main.php's header, shared by all sections.
 *
 * On the phone the bar is a different instrument: the name and the logout icon
 * gave way to three buttons — «+» (новый пациент), поиск and аватар. The first
 * two drive the patients page through App (the page owns its state; the bar
 * only asks), the avatar opens the one menu that holds everything else:
 * sections, the admin page and logout.
 */
export default function TopBar({
  user,
  meta,
  section,
  onSection,
  onLogout,
  onMobileSearch,
  onMobileAdd,
  onOpenPatient,
}: {
  user: SessionUser;
  meta: Meta;
  section: Section;
  onSection: (s: Section) => void;
  onLogout: () => void;
  onMobileSearch: (f: MobileSearch) => void;
  onMobileAdd: () => void;
  /** «Карточка пациента» из карточки напоминания. */
  onOpenPatient?: (patId: number) => void;
}) {
  const isMobile = useIsMobile();
  const canOperations = user.can_op_list || user.is_admin;

  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);

  // Поиск: те же фильтры, что на странице пациентов, но в выпадашке.
  const [q, setQ] = useState("");
  const [diag, setDiag] = useState(false);
  const [okt, setOkt] = useState(false);
  const [doctor, setDoctor] = useState("");
  const [month, setMonth] = useState(0);
  const [year, setYear] = useState(0);
  const [doctors, setDoctors] = useState<string[]>([]);

  useEffect(() => {
    if (!searchOpen || doctors.length) return;
    api
      .doctors()
      .then((r) => setDoctors(r.doctors))
      .catch(() => setDoctors([]));
  }, [searchOpen, doctors.length]);

  useEffect(() => {
    if (!menuOpen && !searchOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (box.current?.contains(e.target as Node)) return;
      setMenuOpen(false);
      setSearchOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [menuOpen, searchOpen]);

  const go = (s: Section) => {
    setMenuOpen(false);
    onSection(s);
  };

  function applySearch() {
    setSearchOpen(false);
    onMobileSearch({ query: q.trim(), diag, okt, doctor, month, year });
  }

  /** Сброс — как «×» у поиска на большом экране: чистит всё разом. */
  function resetSearch() {
    setQ("");
    setDiag(false);
    setOkt(false);
    setDoctor("");
    setMonth(0);
    setYear(0);
    setSearchOpen(false);
    onMobileSearch({ query: "", diag: false, okt: false, doctor: "", month: 0, year: 0 });
  }

  const sections: { key: Section; label: string; show: boolean }[] = [
    { key: "patients", label: "Пациенты", show: true },
    { key: "operations", label: "Оперсписок", show: canOperations },
    // Склад видят все: остатки — справка для всех, приход и расход — право «Склад».
    { key: "stock", label: "Склад", show: true },
    { key: "appointments", label: "Запись", show: true },
  ];

  return (
    <div className="top">
      <div className="container">
        <div className="top-logo">
          {/* Клик по логотипу — сброс текущего раздела к начальному виду:
              тот же механизм, что и повторный клик по активной вкладке. */}
          <a
            href="/"
            title="Сбросить страницу к начальному виду"
            onClick={(e) => {
              e.preventDefault();
              onSection(section);
            }}
          >
            <div />
          </a>
        </div>
        <div className="top-menu">
          {sections
            .filter((s) => s.show)
            .map((s) => (
              <a
                key={s.key}
                href={`#${s.key}`}
                className={section === s.key ? "active" : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  onSection(s.key);
                }}
              >
                {s.label}
              </a>
            ))}
          {user.is_admin && (
            <a
              href="#admin"
              className={section === "admin" ? "active" : undefined}
              onClick={(e) => {
                e.preventDefault();
                onSection("admin");
              }}
            >
              Админ
            </a>
          )}
        </div>

        {!isMobile ? (
          <div className="top-manager">
            {(user.is_admin || user.can_update) && (
              <a href="/update" className="top-update" title="Обновление системы">
                Обновление
              </a>
            )}
            {/* Системному администратору — без админки (у администратора
                помощник в «Администрирование → ИИ-помощник»). */}
            {AI_ENABLED && user.can_update && !user.is_admin && (
              <a href="/ai" className="top-update" title="Настройки ИИ-помощника">
                ИИ-помощник
              </a>
            )}
            {/* Напоминания оперсписка — у тех, кто его ведёт (ReminderBell). */}
            {canOperations && <ReminderBell user={user} onOpenPatient={onOpenPatient} />}
            <strong>{user.fio}</strong>
            <a
              href="#logout"
              title="Выйти"
              onClick={(e) => {
                e.preventDefault();
                onLogout();
              }}
            />
          </div>
        ) : (
          <div className="top-mobile" ref={box}>
            {user.can_add && (
              <div
                className="top-mob-btn top-mob-plus"
                title="Добавить пациента"
                onClick={() => {
                  setMenuOpen(false);
                  setSearchOpen(false);
                  onMobileAdd();
                }}
              >
                +
              </div>
            )}
            <div
              className={`top-mob-btn top-mob-search${searchOpen ? " active" : ""}`}
              title="Поиск пациентов"
              onClick={() => {
                setMenuOpen(false);
                setSearchOpen((v) => !v);
              }}
            />
            {canOperations && <ReminderBell user={user} mobile onOpenPatient={onOpenPatient} />}
            <div
              className={`top-mob-avatar${menuOpen ? " active" : ""}`}
              title={user.fio}
              onClick={() => {
                setSearchOpen(false);
                setMenuOpen((v) => !v);
              }}
            >
              {(user.fio.trim()[0] ?? "?").toUpperCase()}
            </div>

            {menuOpen && (
              <div className="top-mob-menu">
                {/* Имя — заголовок меню, а не пункт: нажимать на себя некуда. */}
                <div className="top-mob-menu-name">{user.fio}</div>
                {sections
                  .filter((s) => s.show)
                  .map((s) => (
                    <div
                      key={s.key}
                      className={`top-mob-menu-item${section === s.key ? " active" : ""}`}
                      onClick={() => go(s.key)}
                    >
                      {s.label}
                    </div>
                  ))}
                {user.is_admin && (
                  <div
                    className={`top-mob-menu-item${section === "admin" ? " active" : ""}`}
                    onClick={() => go("admin")}
                  >
                    Администрирование
                  </div>
                )}
                {(user.is_admin || user.can_update) && (
                  <a className="top-mob-menu-item" href="/update">
                    Обновление системы
                  </a>
                )}
                {AI_ENABLED && user.can_update && !user.is_admin && (
                  <a className="top-mob-menu-item" href="/ai">
                    ИИ-помощник
                  </a>
                )}
                <div className="top-mob-menu-sep" />
                <div
                  className="top-mob-menu-item top-mob-menu-exit"
                  onClick={() => {
                    setMenuOpen(false);
                    onLogout();
                  }}
                >
                  Выйти
                </div>
              </div>
            )}

            {searchOpen && (
              <div className="top-mob-menu top-mob-search-drop">
                <input
                  type="text"
                  placeholder={diag ? "Диагноз" : "Ф.И.О., телефон, критерий поиска"}
                  value={q}
                  autoFocus
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applySearch()}
                />
                <div className="top-mob-search-pills">
                  <div
                    className={`pat-filters-okt pat-filters-diag${diag ? " selected" : ""}`}
                    title="Искать набранное в диагнозах пациентов"
                    onClick={() => setDiag(!diag)}
                  >
                    <input type="checkbox" checked={diag} readOnly style={{ pointerEvents: "none" }} />
                    Диагноз
                  </div>
                  <div
                    className={`pat-filters-okt${okt ? " selected" : ""}`}
                    onClick={() => setOkt(!okt)}
                  >
                    <input type="checkbox" checked={okt} readOnly style={{ pointerEvents: "none" }} />
                    ОКТ
                  </div>
                </div>
                <select value={doctor} onChange={(e) => setDoctor(e.target.value)}>
                  <option value="">Врач</option>
                  {doctors.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                <div className="top-mob-search-dates">
                  <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                    <option value={0}>Месяц</option>
                    {Object.entries(meta.months).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                  <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    <option value={0}>Год</option>
                    {meta.years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="top-mob-search-actions">
                  <div className="button green" onClick={applySearch}>
                    Найти
                  </div>
                  {!!(q || diag || okt || doctor || month || year) && (
                    <u className="hist-link" onClick={resetSearch}>
                      сбросить
                    </u>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
